import { DateTime } from "luxon";

import { Task } from "@/data/models/Task";
import {
  SchedulingSettings,
  calculateWorkDayStart,
} from "@/data/models/SchedulingSettings";
import { HourAndMinute } from "@/data/models/HourAndMinute";
import { TimeSlot, getDayId } from "./TimeSlot";
import { getDateFromHoursAndMinuteString } from "@/utils/DateHelper";

enum Mode {
  DEFAULT,
  FIXED,
  START_AT,
}

export class TimeSlotsOfDayIterator {
  private currentDateTime: DateTime | null = null;
  private dayId = "";

  private startOfWorkDay: HourAndMinute;
  private endOfWorkDay: HourAndMinute;

  private maxTimeInMinutes: number;
  private minTimeInMinutes: number;

  private stepSizeInMinutes: number;

  private travelTimeFromDepotInMin: number;

  private task: Task;

  private mode: Mode = Mode.DEFAULT;

  constructor(task: Task, schedulingSettings: SchedulingSettings) {
    this.stepSizeInMinutes = schedulingSettings.stepSizeInMin;

    this.startOfWorkDay =
      schedulingSettings.workingHoursSettings.defaultWorkDayStart;

    this.endOfWorkDay =
      schedulingSettings.workingHoursSettings.latestWorkDayEnd;

    const {
      defaultTravelTimeBetweenTaskAndDepotInMin: betweenTaskAndDepot,
    } = schedulingSettings.travelTimeSettings;

    this.travelTimeFromDepotInMin = betweenTaskAndDepot;

    this.maxTimeInMinutes =
      this.endOfWorkDay.hour * 60 +
      this.endOfWorkDay.minute -
      (task.durationInMin + betweenTaskAndDepot);

    this.minTimeInMinutes =
      this.startOfWorkDay.hour * 60 +
      this.startOfWorkDay.minute +
      this.travelTimeFromDepotInMin;

    this.task = task;
  }

  setCurrentDateTime(dateTime: DateTime, forcedCurrentDate?: DateTime) {
    const { targetTime, isTargetTimeFixed } = this.task.planningParameters;
    const now = DateTime.fromJSDate(new Date());
    this.dayId = getDayId(dateTime.toJSDate());

    if (targetTime) {
      if (isTargetTimeFixed) {
        this.mode = Mode.FIXED;
      } else {
        this.mode = Mode.DEFAULT;
      }

      const date = getDateFromHoursAndMinuteString(targetTime);

      if (date) {
        this.currentDateTime = dateTime.set({
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: 0,
          millisecond: 0,
        });

        const isBeforeNow = this.currentDateTime.toMillis() < now.toMillis();

        if (isBeforeNow) {
          if (this.mode == Mode.FIXED) {
            this.currentDateTime = null;
            return;
          }
        } else {
          return;
        }
      }
    }

    this.mode = Mode.DEFAULT;

    this.currentDateTime = forcedCurrentDate ?? this.getWorkDayStart(dateTime);
    const isBeforeNow = this.currentDateTime.toMillis() < now.toMillis();

    if (isBeforeNow) {
      const minutesToAdd =
        Math.floor(now.minute / this.stepSizeInMinutes) *
        this.stepSizeInMinutes;

      this.currentDateTime = now
        .startOf("hour")
        .plus({ minutes: minutesToAdd, hour: 1 });
    }
  }

  getWorkDayStart(dateTime: DateTime) {
    return calculateWorkDayStart(
      dateTime,
      this.startOfWorkDay,
      this.travelTimeFromDepotInMin,
      this.stepSizeInMinutes
    );
  }

  getNextTimeSlot(): TimeSlot | null {
    const currentDateTime = this.currentDateTime;

    if (!currentDateTime) {
      return null;
    }

    const hour = currentDateTime.hour;
    const minute = currentDateTime.minute;

    const timeInMinutes = hour * 60 + minute;
    const isInRange =
      timeInMinutes >= this.minTimeInMinutes &&
      timeInMinutes <= this.maxTimeInMinutes;

    if (this.mode === Mode.DEFAULT && !isInRange) {
      this.currentDateTime = null;
      return null;
    }

    const timeSlot = {
      dayId: this.dayId,
      startTimestamp: currentDateTime.toMillis(),
      durationInMs: this.task.durationInMin * 60000,
    };

    switch (this.mode) {
      case Mode.FIXED: {
        this.currentDateTime = null;
        break;
      }
      case Mode.START_AT: {
        this.currentDateTime = this.getWorkDayStart(currentDateTime);
        break;
      }
      case Mode.DEFAULT: {
        this.currentDateTime = null;
        break;
      }
    }

    return timeSlot;
  }
}
